/**
 * Material imports
 */
import {
  Box,
  Grid,
  LinearProgress,
  Skeleton,
  Typography
} from '@mui/material'

/**
 * Relative exports to root directory
 */
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { useRef, useState } from 'react'
import { useThemeMode } from '../../../theme'
import { TranslatableTypography } from './index'

const getThemeSx = (palette) => ({
  '& .companion-map-grid-wrapper': {
    background: palette.mapBackground,
    border: `3px solid ${palette.mapBackground}`,
    position: 'relative',
    borderRadius: '5px',
    overflow: 'hidden'
  },
  '& .companion-map-image-wrapper': {
    width: '100%',
    background: palette.mapBackground,
    position: 'relative',
    overflow: 'hidden'
  },
  '& .companion-map-floor-title': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    textAlign: 'center',
    color: palette.mapTextColor,
    fontWeight: 700,
    fontSize: '1.3rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '10px 60px',
    background: 'transparent',
    textShadow: '1px 2px 6px rgba(0,0,0,0.95)'
  },
  '& .companion-map-button-left': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '50%',
    height: '100%',
    padding: '10px',
    '& .MuiSvgIcon-root': {
      background: palette.headerColor,
      width: '40px',
      height: '40px',
      borderRadius: '24px',
      fontSize: '18px'
    }
  },
  '& .companion-map-button-right': {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '50%',
    height: '100%',
    textAlign: 'right',
    padding: '10px',
    '& .MuiSvgIcon-root': {
      background: palette.headerColor,
      width: '40px',
      height: '40px',
      borderRadius: '24px',
      fontSize: '18px'
    }
  },
  '& .companion-map-floor-image': {
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  '& .companion-map-legend-wrapper': {
    width: '100%',
    padding: '6px 8px',
    background: palette.mapBackground
  },
  '& .companion-map-legend-content': {
    display: 'flex',
    flexWrap: 'wrap',
    width: '320px',
    margin: '0 auto',
    '@media (max-width: 400px)': {
      width: '100%'
    }
  },
  '& .companion-map-legend-column-wrapper': {
    display: 'flex',
    alignItems: 'baseline',
    padding: '2px 4px',
    width: '160px',
    '@media (max-width: 400px)': {
      width: '50%'
    }
  },
  '& .companion-map-legend-number': {
    minWidth: '36px',
    flexShrink: 0,
    '& p': {
      margin: 0,
      fontStyle: 'italic',
      fontWeight: 900,
      fontSize: '0.85rem',
      color: palette.mapTextColor,
      textTransform: 'uppercase'
    }
  },
  '& .companion-map-legend-title': {
    flex: 1,
    '& p': {
      margin: 0,
      fontWeight: 700,
      fontSize: '0.8rem',
      color: palette.mapTextColor,
      textTransform: 'uppercase',
      letterSpacing: '0.03em'
    }
  }
})

export const Map = (props) => {
  const { data } = props
  const { palette } = useThemeMode()
  const themeSX = getThemeSx(palette)
  const [selected, setSelected] = useState(data?.[0]?.id)

  if (!data?.length) return null

  const idx = Math.max(0, data.findIndex(f => f.id === selected))
  const previous = data[idx > 0 ? idx - 1 : data.length - 1].id
  const next = data[idx < data.length - 1 ? idx + 1 : 0].id

  return (
    <Box sx={themeSX}>
      <MapFloorLegend
        key={selected}
        previous={previous}
        next={next}
        select={(id) => setSelected(id)}
        {...data[idx]}
      />
    </Box>
  )
}

export const MapFloorLegendItem = (props) => {
  const { label, titles } = props

  return (
    <Box className="companion-map-legend-column-wrapper">
      <Box className="companion-map-legend-number">
        <Typography>{label}</Typography>
      </Box>
      <Box className="companion-map-legend-title">
        <TranslatableTypography data={titles} />
      </Box>
    </Box>
  )
}

export const MapFloorLegend = (props) => {
  const { img, titles, legend, previous, next, select } = props
  const [imageLoaded, setImageLoaded] = useState(false)
  const columns = legend.map(l => <MapFloorLegendItem key={l.id} titles={l.titles} label={l.label} />)
  const touchStartXRef = useRef(null)

  const handleTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      touchStartXRef.current = e.touches[0].clientX
    }
  }

  const handleTouchEnd = (e) => {
    if (typeof touchStartXRef.current !== 'number') return
    if (!e.changedTouches?.length) return
    const touchEndX = e.changedTouches[0].clientX
    const diffX = touchEndX - touchStartXRef.current
    if (Math.abs(diffX) > 50) {
      select(diffX < 0 ? next : previous)
    }
    touchStartXRef.current = null
  }

  return (
    <Box onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      <Grid container className="companion-map-grid-wrapper">
        <Grid item xs={12} className="companion-map-image-wrapper">
          <TranslatableTypography data={titles} className="companion-map-floor-title" />
          {!imageLoaded && (
            <Skeleton variant="rectangular" width="100%" sx={{ aspectRatio: '809/821', display: 'block' }} />
          )}
          <img
            src={img}
            alt={titles?.find(t => t.lang === 'en')?.text ?? ''}
            className="companion-map-floor-image"
            onLoad={() => setImageLoaded(true)}
            style={{ display: imageLoaded ? 'block' : 'none' }}
          />
          {!imageLoaded && (
            <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />
          )}
          <Box className="companion-map-button-left">
            <KeyboardArrowLeftIcon onClick={() => select(previous)} />
          </Box>
          <Box className="companion-map-button-right">
            <KeyboardArrowRightIcon onClick={() => select(next)} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box className="companion-map-legend-wrapper">
            <Box className="companion-map-legend-content">
              {columns}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
